import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import PartnerDetails from "./PartnerDetails.tsx";
import { Incident } from "../../models/Incidents";
import CustomTextArea from "../formik/CustomTextArea.tsx";
import {
  IncidentReason,
  IncidentReasonCheckoutSchema,
  IncidentReasonSchema,
} from "../../validation/schemas/IncidentReasonSchema.ts";
import { Chip, Tooltip } from "@mui/material";
import { saveIncident, updateIncident } from "../../services/incident.service.ts";
import { notification } from "../../utils/Notifications.tsx";
import {
  setCurrentIndex,
  setShowModal,
  setRefetch,
} from "../../store/slices/incidenciaSlice.ts";
import { saveCheckout } from "../../services/assistanceCheck.service.ts";
import { RootState } from "../../store/index.ts";
import ModalContainer from "../ModalContainer.tsx";
import ModalCheckDetails from "../catalogo/Sucursales/componentes/ModalCheckDetails.tsx";
import useFetchCheckDetailsTwoIds from "../profile/hooks/useFetchCheckDetailsTwoIds.tsx";
import type { IncidentAfterSavePayload } from "./incidentTableTypes.ts";
import {
  resolveIncidentRealChecks,
  resolveScheduledTimesForDisplay,
} from "../../utils/incidentDisplayTimes.ts";
import { getIncidentRecordBranchId, getIncidentBranchName } from "../../utils/incidentBranchMeta.ts";
import type { BranchReportMeta } from "../../utils/payrollBranchMeta.ts";
import {
  effectiveNightMetaForPartner,
  partnerShiftFromIncidentSchedule,
} from "../../utils/nightScheduleEligibility.ts";
import {
  useIncidentRowExit,
  type IncidentRowExitVariant,
} from "./useIncidentRowExit.ts";

const DEFAULT_BRANCH_META: BranchReportMeta = {
  nightScheduleEnabled: false,
  cutoffMinutes: 3 * 60,
};

const formatDateLabel = (dateStr?: string | Date) => {
  if (!dateStr) return "";
  const raw =
    dateStr instanceof Date
      ? dateStr.toISOString().slice(0, 10)
      : String(dateStr).slice(0, 10);
  const d = new Date(`${raw}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  return d
    .toLocaleDateString("es-MX", {
      day: "numeric",
      month: "short",
      year: "numeric",
    })
    .replace(".", "");  
};

const RowIncident = ({
  record,
  removeRecord,
  index,
  onAfterSave,
  updateIncidentRecord,
  isAdmin = false,
  branchMetaMap = {},
  maxNightCutoffMinutes,
  showBranchName = false,
  branchNameById = {},
}: {
  record: Incident;
  removeRecord: () => void;
  index: number;
  onAfterSave?: (payload: IncidentAfterSavePayload) => void;
  updateIncidentRecord?: (scheduleId: number, updates: { type?: string; reason?: string }) => void;
  isAdmin?: boolean;
  branchMetaMap?: Record<number, BranchReportMeta>;
  /** Jefe directo: mayor corte entre sucursales nocturnas cuando hay colaboradores Nocturno. */
  maxNightCutoffMinutes?: number;
  showBranchName?: boolean;
  branchNameById?: Record<number, string>;
}) => {
  const branchId = getIncidentRecordBranchId(record);
  const branchLabel = showBranchName
    ? getIncidentBranchName(record, branchNameById)
    : "";
  const branchMeta = branchMetaMap[branchId] ?? DEFAULT_BRANCH_META;
  const partnerShift = partnerShiftFromIncidentSchedule(record?.schedule);
  const effectiveMeta = effectiveNightMetaForPartner(branchMeta, partnerShift);
  const nightScheduleEnabled = effectiveMeta.nightScheduleEnabled;
  const nightCutoffMinutes = effectiveMeta.cutoffMinutes;
  const { refetch, quickAnswer } = useSelector(
    (state: RootState) => state.incidencias
  );
  const dispatch = useDispatch();
  const [disabled, setDisabled] = React.useState(false);
  const { rowClassName, isExiting, playExitAndThen } = useIncidentRowExit();

  const isSyntheticUnscheduledSchedule = React.useMemo(() => {
    const schedule = record?.schedule as {
      positionX?: number;
      positionY?: number;
      unscheduled?: boolean;
    };
    return (
      record?.status === "No programado" ||
      Number(schedule?.positionX) === -1 ||
      Number(schedule?.positionY) === -1 ||
      schedule?.unscheduled === true
    );
  }, [record?.status, record?.schedule]);

  const scheduled = React.useMemo(() => {
    if (isSyntheticUnscheduledSchedule) {
      const schedule = record.schedule as {
        assistanceCheck?: { checkTime?: string };
        assistanceCheckout?: { checkTime?: string };
        checkIn?: { checkTime?: string };
        checkOut?: { checkTime?: string };
      };
      const checkIn =
        (record as any).checkIn ??
        schedule?.assistanceCheck ??
        schedule?.checkIn;
      const checkOut =
        (record as any).checkOut ??
        schedule?.assistanceCheckout ??
        schedule?.checkOut;

      if (checkIn?.checkTime || checkOut?.checkTime) {
        return {
          entrada: checkIn?.checkTime ? String(checkIn.checkTime) : "--",
          salida: checkOut?.checkTime ? String(checkOut.checkTime) : "--",
        };
      }

      const fromStoredTimes = resolveScheduledTimesForDisplay(
        record?.schedule?.startTime,
        record?.schedule?.endTime,
        nightScheduleEnabled
      );
      if (fromStoredTimes.entrada || fromStoredTimes.salida) {
        return {
          entrada: fromStoredTimes.entrada || "--",
          salida: fromStoredTimes.salida || "--",
        };
      }

      return { entrada: "--", salida: "--" };
    }
    return resolveScheduledTimesForDisplay(
      record?.schedule?.startTime,
      record?.schedule?.endTime,
      nightScheduleEnabled
    );
  }, [
    record?.schedule?.startTime,
    record?.schedule?.endTime,
    (record.schedule as any)?.assistanceCheck,
    (record.schedule as any)?.assistanceCheckout,
    record?.schedule?.checkIn,
    record?.schedule?.checkOut,
    (record as any).checkIn,
    (record as any).checkOut,
    nightScheduleEnabled,
    isSyntheticUnscheduledSchedule,
  ]);

  const { checkIn: realCheckIn, checkOut: realCheckOut } = React.useMemo(
    () =>
      resolveIncidentRealChecks(record, {
        branchMeta,
        shiftName: partnerShift,
        maxNightCutoffMinutes,
      }),
    [record, branchId, branchMeta, partnerShift, maxNightCutoffMinutes]
  );

  const sortedChecks = React.useMemo(() => {
    const checks = (record?.schedule as any)?.checks || [];
    if (!Array.isArray(checks)) return [];
    return [...checks].sort((a: any, b: any) => {
      const aDateTime = new Date(
        `${String(a?.checkDate || "")}T${String(a?.checkTime || "00:00:00")}`,
      ).getTime();
      const bDateTime = new Date(
        `${String(b?.checkDate || "")}T${String(b?.checkTime || "00:00:00")}`,
      ).getTime();

      if (!Number.isNaN(aDateTime) && !Number.isNaN(bDateTime)) {
        return aDateTime - bDateTime;
      }

      const aTime = String(a?.checkTime || "");
      const bTime = String(b?.checkTime || "");
      return aTime.localeCompare(bTime);
    });
  }, [record]);
  const effectiveCheckIn: any =
    record?.status === "No tuvo chequeo de salida"
      ? (record?.checkIn ??
          (record?.schedule as any)?.assistanceCheck ??
          realCheckIn)
      : realCheckIn;
  const effectiveCheckOut: any = React.useMemo(() => {
    if (record?.status === "No tuvo chequeo de salida") {
      const explicitOut =
        record?.checkOut ??
        (record?.schedule as any)?.assistanceCheckout ??
        null;
      const cin = effectiveCheckIn;
      if (!explicitOut?.checkTime) {
        return null;
      }
      if (
        cin?.id != null &&
        explicitOut.id != null &&
        cin.id === explicitOut.id
      ) {
        return null;
      }
      if (String(explicitOut.checkTime) === String(cin?.checkTime || "")) {
        return null;
      }
      return explicitOut;
    }
    const out = realCheckOut ?? record?.checkOut ?? null;
    const cin = effectiveCheckIn;
    if (!out || !cin) {
      return out;
    }
    if (out.id != null && cin.id != null && out.id === cin.id) {
      return realCheckOut ?? null;
    }
    if (String(out.checkTime || "") === String(cin.checkTime || "")) {
      return realCheckOut ?? null;
    }
    return out;
  }, [
    realCheckOut,
    record?.checkOut,
    record?.status,
    record?.schedule,
    effectiveCheckIn,
  ]);
  const checkInDate =
    effectiveCheckIn?.checkDate ?? record?.checkIn?.checkDate;
  const checkOutDate =
    effectiveCheckOut?.checkDate ?? record?.checkOut?.checkDate;
  const isCrossDayCheck =
    Boolean(checkInDate) &&
    Boolean(checkOutDate) &&
    String(checkInDate) !== String(checkOutDate);

  const {
    checkDetails,
    openCheckDetails,
    setOpenCheckDetails,
    fetchCheckDetails,
    profileImage,
  } = useFetchCheckDetailsTwoIds();

  const handleCodeCheckClick = async () => {
    const inId = effectiveCheckIn?.id ?? record.checkIn?.id;
    const outId = effectiveCheckOut?.id ?? record.checkOut?.id;
    const partnerId = record.schedule?.partner?.id;

    if (inId && partnerId) {
      await fetchCheckDetails(
        String(inId),
        outId ? String(outId) : undefined,
        String(partnerId)
      );
    }
  };

  const isSubmittingRef = React.useRef(false);
  
  // Detectar si es una incidencia resuelta
  const isResolved = (record as any).resolved === true || Boolean((record as any).incidentId);
  const incidentId = (record as any).incidentId;
  const savedReason = (record as any).reason || "";
  const savedType = (record as any).type || "";
  const authorizeBy = (record as any).authorizeBy;
  
  // Obtener nombre completo del usuario que resolvió
  const resolverName = authorizeBy
    ? `${authorizeBy.name || ""} ${authorizeBy.lastName || ""} ${authorizeBy.secondLastName || ""}`.trim()
    : "Usuario desconocido";
  
  // Si no es admin y está resuelta, aplicar estilos de solo lectura
  const isReadOnly = isResolved && !isAdmin;

  const buildAfterSavePayload = (
    values: { type: string; reason: string },
    incidentId?: number
  ): IncidentAfterSavePayload => ({
    kind: "schedule",
    scheduleId: record.schedule.id,
    status: record.status,
    record,
    type: values.type,
    reason: values.reason,
    incidentId,
  });

  const finishSave = (
    response: { id?: number; scheduleId?: number; message?: string } | undefined,
    values: { type: string; reason: string }
  ) => {
    const exitVariant: IncidentRowExitVariant =
      values.type === "Rechazar" ? "reject" : "accept";

    const completeRemoval = () => {
      if (response?.message === "Ya existe una incidencia") {
        console.warn("⚠️ Incidencia ya existe en backend, ocultando de la tabla");
        if (onAfterSave) {
          onAfterSave(buildAfterSavePayload(values, response?.id));
        } else {
          removeRecord();
        }
        return;
      }

      if (onAfterSave) {
        onAfterSave(
          buildAfterSavePayload(values, response?.id ?? (record as any).incidentId)
        );
      } else {
        removeRecord();
        dispatch(setRefetch(!refetch));
      }
    };

    if (response?.message === "Ya existe una incidencia") {
      notification("Info", "La incidencia ya fue procesada anteriormente", "info");
    }
    playExitAndThen(exitVariant, completeRemoval);
  };

  const formik = useFormik({
    validationSchema:
      record.status != "No tuvo chequeo de salida"
        ? IncidentReasonSchema
        : IncidentReasonCheckoutSchema,
    initialValues: {
      ...IncidentReason,
      reason: isResolved ? savedReason : IncidentReason.reason,
      type: isResolved ? savedType : IncidentReason.type,
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (isSubmittingRef.current || disabled) return;

      
      if (!values.type) {
        notification("Error", "Debes seleccionar Aceptar o Rechazar", "danger");
        return;
      }

      isSubmittingRef.current = true;
      setDisabled(true);

      try {
        // Si es una incidencia resuelta, usar updateIncident (solo si es admin)
        if (isResolved && incidentId) {
          // Si no es admin, no permitir editar incidencias resueltas
          if (!isAdmin) {
            notification("Info", "No tienes permisos para editar incidencias resueltas", "info");
            setDisabled(false);
            isSubmittingRef.current = false;
            return;
          }
          const { checkout, ...info } = values;
          const data = {
            ...info,
            status: record.status,
            schedule: record.schedule.id,
            checkIn: effectiveCheckIn?.id ?? record?.checkIn?.id ?? -1,
            checkOut: effectiveCheckOut?.id ?? record?.checkOut?.id ?? -1,
          };

          const response = await updateIncident(incidentId, data);
          
          // Actualizar el record localmente inmediatamente
          if (updateIncidentRecord) {
            updateIncidentRecord(record.schedule.id, {
              type: values.type,
              reason: values.reason,
            });
          }
          
          notification("Éxito", "Incidencia actualizada correctamente", "success");
          
          if (onAfterSave) {
            onAfterSave(buildAfterSavePayload(values, incidentId));
          } else {
            dispatch(setRefetch(!refetch));
          }

          setDisabled(false);
          isSubmittingRef.current = false;
          return;
        }

        // Lógica original para incidencias pendientes
        if (record.status === "No tuvo chequeo de salida") {
          const checkout = await saveCheckout({
            checkDate: record.checkOut.checkDate,
            checkTime: record.checkOut.checkTime,
            partner: record.schedule.partner.id,
            schedule: record.schedule.id,
          });

          const { checkout: _, ...info } = values;
          const data = {
            ...info,
            status: record.status,
            schedule: record.schedule.id,
            checkIn: effectiveCheckIn?.id ?? record?.checkIn?.id ?? -1,
            checkOut: checkout?.id ? checkout.id : record?.checkOut?.id ?? -1,
          };

        const response = await saveIncident(data);
        finishSave(response, values);
        setDisabled(false);
        isSubmittingRef.current = false;
        return;
        }

        const { checkout, ...info } = values;
        const data = {
          ...info,
          status: record.status,
          schedule: record.schedule.id,
          checkIn: effectiveCheckIn?.id ?? record?.checkIn?.id ?? -1,
          checkOut: effectiveCheckOut?.id ?? record?.checkOut?.id ?? -1,
        };

        const response = await saveIncident(data);
        finishSave(response, values);
        setDisabled(false);
        isSubmittingRef.current = false;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Error al guardar incidencia";
        
        
        if (errorMessage.includes("Ya existe") || errorMessage.includes("ya existe")) {
          console.warn("⚠️ Error: Incidencia ya existe, ocultando de la tabla:", errorMessage);
          const exitVariant: IncidentRowExitVariant =
            formik.values.type === "Rechazar" ? "reject" : "accept";
          playExitAndThen(exitVariant, () => {
            if (onAfterSave) {
              onAfterSave(buildAfterSavePayload(formik.values));
            } else {
              removeRecord();
            }
          });
          notification("Info", "La incidencia ya fue procesada anteriormente", "info");
        } else {
          notification("Error", errorMessage, "danger");
        }
        
        setDisabled(false);
        isSubmittingRef.current = false;
        
        return;
      }
    },
  });

  React.useEffect(() => {
    if (record?.checkOut?.id !== -1 && record?.checkOut?.id !== undefined) {
      formik.setFieldValue("checkout", record?.checkOut?.id);
    }
    
    // Si es una incidencia resuelta, cargar los valores guardados
    if (isResolved) {
      const currentReason = (record as any).reason || "";
      const currentType = (record as any).type || "";
      if (currentReason !== formik.values.reason) {
        formik.setFieldValue("reason", currentReason);
      }
      if (currentType !== formik.values.type) {
        formik.setFieldValue("type", currentType);
      }
    }
  }, [record, isResolved]);

  return (
    <React.Fragment>
      {/* {JSON.stringify(record)} */}
      <tr
        className={`text-center${rowClassName ? ` ${rowClassName}` : ""}`}
        style={{
          backgroundColor: index % 2 === 0 ? "white" : "#f8f9fa",
          transition: isExiting ? "none" : "all 0.2s ease",
          borderBottom: "1px solid #dee2e6",
          opacity: isReadOnly ? 0.6 : 1,
          filter: isReadOnly ? "grayscale(0.3)" : "none",
        }}
        onMouseEnter={(e) => {
          if (!isReadOnly && !isExiting) {
            e.currentTarget.style.backgroundColor = "#e9ecef";
          }
        }}
        onMouseLeave={(e) => {
          if (!isExiting) {
            e.currentTarget.style.backgroundColor =
              index % 2 === 0 ? "white" : "#f8f9fa";
          }
        }}
      >
        <td
          style={{
            padding: "6px 8px",
            fontSize: "12px",
            borderLeft: "none",
            borderRight: "none",
            backgroundColor: "transparent",
            textAlign: "center",
          }}
        >
          <PartnerDetails record={record} branchName={branchLabel || undefined} />
        </td>
        <td
          style={{
            padding: "6px 8px",
            fontSize: "12px",
            fontWeight: "bold",
            color: isReadOnly ? "#6c757d" : "#28a745",
            borderLeft: "none",
            borderRight: "none",
            backgroundColor: "transparent",
          }}
        >
          {scheduled.entrada}
        </td>
        <td
          style={{
            padding: "6px 8px",
            fontSize: "12px",
            fontWeight: "bold",
            color: isReadOnly ? "#6c757d" : "#28a745",
            borderLeft: "none",
            borderRight: "none",
            backgroundColor: "transparent",
          }}
        >
          {scheduled.salida}
        </td>
        <td
          style={{
            padding: "6px 8px",
            fontSize: "12px",
            fontWeight: "bold",
            color: isReadOnly ? "#adb5bd" : "#f0ad4e",
            borderLeft: "none",
            borderRight: "none",
            backgroundColor: "transparent",
          }}
        >
          <div>{effectiveCheckIn?.checkTime}</div>
        </td>
        <td
          style={{
            padding: "6px 8px",
            fontSize: "12px",
            fontWeight: "bold",
            color: isReadOnly ? "#adb5bd" : "#f0ad4e",
            borderLeft: "none",
            borderRight: "none",
            backgroundColor: "transparent",
          }}
        >
          <div>{effectiveCheckOut?.checkTime}</div>
          {isCrossDayCheck && checkOutDate && (
            <div style={{ fontSize: "10px", fontWeight: 500, color: "#adb5bd" }}>
              {formatDateLabel(checkOutDate)}
            </div>
          )}
        </td>
        <td
          className="text-center"
          style={{
            width: "45%",
            padding: "6px 8px",
            borderLeft: "none",
            borderRight: "none",
            backgroundColor: "transparent",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyItems: "center",
              justifyContent: "center",
            }}
          >
            <div className="w-100 mt-1 fs-12px fw-800 bg d-flex justify-content-center align-items-center gap-2 flex-wrap">
              <span style={{ fontSize: "12px", color: isReadOnly ? "#6c757d" : "inherit" }}>{record.status}</span>
              {isResolved && (
                <Tooltip 
                  title={`Resuelta por: ${resolverName} - ${savedType || ""}`} 
                  arrow
                  placement="top"
                >
                  <Chip
                    label={resolverName}
                    size="small"
                    variant="outlined"
                    style={{ 
                      fontSize: "9px", 
                      height: "18px", 
                      borderColor: isReadOnly ? "#adb5bd" : "#6c757d",
                      color: isReadOnly ? "#adb5bd" : "#6c757d"
                    }}
                  />
                </Tooltip>
              )}
              {record.isCodeChecked && !isReadOnly && (
                <Chip
                  label="Asistencia con código"
                  size="small"
                  color="primary"
                  variant="outlined"
                  style={{ fontSize: "10px", cursor: "pointer", height: "20px" }}
                  onClick={handleCodeCheckClick}
                />
              )}
            </div>
            <div className="w-100 d-flex justify-content-center align-items-center gap-2" style={{ marginTop: "4px" }}>
              <div
                className={`row col-md-${
                  record.status === "No tuvo chequeo de salida" ? 10 : 12
                }`}
                style={{ margin: 0 }}
              >
                <div style={{ width: "100%" }}>
                  <textarea
                    disabled={disabled || isReadOnly}
                    className="form-control"
                    style={{
                      fontSize: "12px",
                      borderRadius: "6px",
                      border:
                        formik.touched.reason && formik.errors.reason
                          ? "1px solid #dc3545"
                          : "1px solid #e9ecef",
                      padding: "6px 10px",
                      minHeight: "45px",
                      resize: "vertical",
                      transition: "all 0.2s ease",
                      backgroundColor: disabled || isReadOnly ? "#f8f9fa" : "white",
                      color: disabled || isReadOnly ? "#6c757d" : "#212529",
                      cursor: isReadOnly ? "not-allowed" : "text",
                    }}
                    id="reason"
                    name="reason"
                    placeholder="Escribe la razón de la incidencia..."
                    onChange={formik.handleChange}
                    value={formik.values.reason}
                    onFocus={(e) => {
                      if (formik.touched.reason && formik.errors.reason) {
                        e.target.style.borderColor = "#dc3545";
                        e.target.style.boxShadow =
                          "0 0 0 3px rgba(220, 53, 69, 0.1)";
                      } else {
                        e.target.style.borderColor = "#50B7C0";
                        e.target.style.boxShadow =
                          "0 0 0 3px rgba(80, 183, 192, 0.1)";
                      }
                    }}
                    onBlur={(e) => {
                      if (formik.touched.reason && formik.errors.reason) {
                        e.target.style.borderColor = "#dc3545";
                        e.target.style.boxShadow =
                          "0 0 0 3px rgba(220, 53, 69, 0.1)";
                      } else {
                        e.target.style.borderColor = "#e9ecef";
                        e.target.style.boxShadow = "none";
                      }
                      formik.handleBlur(e);
                    }}
                    maxLength={200}
                  />
                  {formik.touched.reason && formik.errors.reason && (
                    <div
                      style={{
                        color: "#dc3545",
                        fontSize: "12px",
                        marginTop: "4px",
                        marginLeft: "4px",
                      }}
                    >
                      {String(formik.errors.reason || "")}
                    </div>
                  )}
                </div>
              </div>
              {record.status === "No tuvo chequeo de salida" && !isReadOnly && (
                <div className="row col-md-2" style={{ margin: 0 }}>
                  <button
                    className="incidents-btn-clock"
                    style={{
                      padding: "6px 12px",
                      fontSize: "12px",
                      fontWeight: "600",
                      borderRadius: "50px",
                      border: "none",
                      backgroundColor: formik.errors.checkout
                        ? "#dc3545"
                        : "#50B7C0",
                      color: "white",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      minWidth: "70px",
                    }}
                    onClick={() => {
                      dispatch(setShowModal(true));
                      dispatch(setCurrentIndex(index));
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 8px rgba(0,0,0,0.15)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 2px 4px rgba(0,0,0,0.1)";
                    }}
                  >
                    <i className="fa fa-clock me-1"></i>
                    {record?.checkOut?.checkTime ?? "00:00"}
                  </button>
                </div>
              )}
            </div>
            <div
              className="invalid-feedback text-center"
              style={{
                marginTop: "1px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginLeft: "0.25em",
                textAlign: "center",
              }}
            >
              {Boolean(formik.touched.checkout && formik.errors.checkout) && (
                <>{formik.errors.checkout}</>
              )}
            </div>
            {!isReadOnly && (
              <div
                className="w-100 mt-2 fs-12px fw-800 bg gap-2 d-flex justify-content-center align-items-center flex-wrap"
                style={{ maxWidth: "100%", marginTop: "4px" }}
              >
                {quickAnswer.map((item: string, index: number) => (
                  <Chip
                    label={item}
                    key={index}
                    size="small"
                    className="incidents-chip-quick-answer"
                    style={{ cursor: "pointer", fontSize: "14px" , padding: "10px" }}
                    onClick={() => formik.setFieldValue("reason", item)}
                  />
                ))}
              </div>
            )}
          </div>
        </td>

        <td
          style={{
            padding: "6px 8px",
            backgroundColor: "transparent",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "6px",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            {isResolved && savedType && (
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  color: isReadOnly 
                    ? (savedType === "Aceptar" ? "#6c757d" : "#adb5bd")
                    : (savedType === "Aceptar" ? "#28a745" : "#dc3545"),
                  marginBottom: "4px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  padding: "2px 8px",
                  borderRadius: "4px",
                  backgroundColor: isReadOnly
                    ? "#e9ecef"
                    : (savedType === "Aceptar" ? "rgba(40, 167, 69, 0.1)" : "rgba(220, 53, 69, 0.1)"),
                }}
              >
                {savedType === "Aceptar" ? "Aceptado" : "Rechazado"}
              </div>
            )}
            <div style={{ display: "flex", gap: "6px" }}>
            <Tooltip title={isResolved && savedType === "Aceptar" ? "Aceptar (Pagable) - Actual" : "Aceptar (Pagable)"} arrow>
              <button
                type="button"
                className="incidents-btn-action incidents-btn-aceptar"
                onClick={async () => {
                  await formik.setFieldValue("type", "Aceptar", false);
                  formik.handleSubmit();
                }}
                disabled={disabled || isReadOnly}
                style={{
                  padding: "6px 8px",
                  fontSize: "14px",
                  borderRadius: "50%",
                  border: isReadOnly
                    ? "2px solid #adb5bd"
                    : (isResolved && savedType === "Aceptar" 
                    ? "4px solid #28a745" 
                    : isResolved 
                    ? "2px solid #dee2e6" 
                    : "none"),
                  backgroundColor: isReadOnly ? "#adb5bd" : (isResolved && savedType === "Aceptar" ? "#28a745" : "#50B7C0"),
                  color: "white",
                  cursor: disabled || isReadOnly ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: isReadOnly
                    ? "none"
                    : (isResolved && savedType === "Aceptar" 
                    ? "0 0 0 4px rgba(40, 167, 69, 0.4), 0 4px 8px rgba(40, 167, 69, 0.3)" 
                    : "0 2px 4px rgba(80, 183, 192, 0.2)"),
                  opacity: disabled || isReadOnly ? 0.6 : 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: isResolved && savedType === "Aceptar" && !isReadOnly ? "38px" : "32px",
                  height: isResolved && savedType === "Aceptar" && !isReadOnly ? "38px" : "32px",
                  transform: isResolved && savedType === "Aceptar" && !isReadOnly ? "scale(1.1)" : "scale(1)",
                  position: "relative",
                }}
                onMouseEnter={(e) => {
                  if (!disabled && !isReadOnly) {
                    if (isResolved && savedType === "Aceptar") {
                      e.currentTarget.style.transform = "scale(1.15)";
                    } else {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 8px rgba(80, 183, 192, 0.3)";
                      e.currentTarget.style.backgroundColor = "#3A9BA3";
                    }
                  }
                }}
                onMouseLeave={(e) => {
                  if (!disabled && !isReadOnly) {
                    if (isResolved && savedType === "Aceptar") {
                      e.currentTarget.style.transform = "scale(1.1)";
                    } else {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 2px 4px rgba(80, 183, 192, 0.2)";
                      e.currentTarget.style.backgroundColor = "#50B7C0";
                    }
                  }
                }}
              >
                <i className="fa-solid fa-check"></i>
              </button>
            </Tooltip>

            <Tooltip title={isResolved && savedType === "Rechazar" ? "Rechazar (No pagable) - Actual" : "Rechazar (No pagable)"} arrow>
              <button
                type="button"
                className="incidents-btn-action incidents-btn-rechazar"
                onClick={async () => {
                  await formik.setFieldValue("type", "Rechazar", false);
                  formik.handleSubmit();
                }}
                disabled={disabled || isReadOnly}
                style={{
                  padding: "6px 8px",
                  fontSize: "14px",
                  borderRadius: "50%",
                  border: isReadOnly
                    ? "2px solid #adb5bd"
                    : (isResolved && savedType === "Rechazar" 
                    ? "4px solid #dc3545" 
                    : isResolved 
                    ? "2px solid #dee2e6" 
                    : "none"),
                  backgroundColor: isReadOnly ? "#adb5bd" : "#DC2C27",
                  color: "white",
                  cursor: disabled || isReadOnly ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: isReadOnly
                    ? "none"
                    : (isResolved && savedType === "Rechazar" 
                    ? "0 0 0 4px rgba(220, 53, 69, 0.4), 0 4px 8px rgba(220, 53, 69, 0.3)" 
                    : "0 2px 4px rgba(220, 44, 39, 0.2)"),
                  opacity: disabled || isReadOnly ? 0.6 : 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: isResolved && savedType === "Rechazar" && !isReadOnly ? "38px" : "32px",
                  height: isResolved && savedType === "Rechazar" && !isReadOnly ? "38px" : "32px",
                  transform: isResolved && savedType === "Rechazar" && !isReadOnly ? "scale(1.1)" : "scale(1)",
                  position: "relative",
                }}
                onMouseEnter={(e) => {
                  if (!disabled && !isReadOnly) {
                    if (isResolved && savedType === "Rechazar") {
                      e.currentTarget.style.transform = "scale(1.15)";
                    } else {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 8px rgba(220, 44, 39, 0.3)";
                      e.currentTarget.style.backgroundColor = "#B82420";
                    }
                  }
                }}
                onMouseLeave={(e) => {
                  if (!disabled && !isReadOnly) {
                    if (isResolved && savedType === "Rechazar") {
                      e.currentTarget.style.transform = "scale(1.1)";
                    } else {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 2px 4px rgba(220, 44, 39, 0.2)";
                      e.currentTarget.style.backgroundColor = "#DC2C27";
                    }
                  }
                }}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </Tooltip>
            </div>
          </div>
        </td>
      </tr>

      <ModalContainer
        open={openCheckDetails}
        title="Historial de chequeos"
        closeFn={() => setOpenCheckDetails(false)}
        size="xl"
      >
        <ModalCheckDetails
          partnerId={record.schedule?.partner?.id}
          setShowModal={setOpenCheckDetails}
          checkDetails={checkDetails}
          profileImage={profileImage}
        />
      </ModalContainer>
    </React.Fragment>
  );
};

export default RowIncident;
